import { useCallback } from "react";
import { toast } from "sonner"; // Changed import

export const useDepartmentOperations = ({
  departmentForm,
  editDepartmentData,
  selectedDepartmentId,
  academyId,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  refetchDepartments,
  showSuccessToast,
  showErrorAlert,
  setFormError,
  resetDepartmentForm,
  setShowModalDepartment,
  setShowEditDepartmentModal,
  setEditDepartmentData,
  setOriginalDepartmentData,
  setSelectedDepartmentId,
  departments = [],
}) => {
  const handleCreateDepartment = useCallback(async () => {
    // Clear any previous errors
    setFormError(null);

    // Validate department name
    if (!departmentForm.name?.trim()) {
      setFormError("Department name is required");
      return;
    }

    // Check if trying to create a maintenance department
    if (departmentForm.name?.trim().toLowerCase() === "maintenance") {
      // Check if maintenance department already exists
      const maintenanceExists = departments?.some(
        (dept) => dept.name?.toLowerCase() === "maintenance",
      );

      if (maintenanceExists) {
        setFormError(
          "A 'maintenance' department already exists. Only one maintenance department is allowed.",
        );
        return;
      }
      // If no maintenance exists, allow creation (continue with normal flow)
    }

    // Validate department description
    if (!departmentForm.description?.trim()) {
      setFormError("Department description is required");
      return;
    }

    // Department is now shared across all academies, no academyId needed
    const departmentData = {
      name: departmentForm.name,
      description: departmentForm.description,
    };

    try {
      const res = await createDepartment(departmentData).unwrap();
      toast.success("Department successfully added!"); // Changed to toast.success
      resetDepartmentForm();
      setShowModalDepartment(false);
      await refetchDepartments();
    } catch (error) {
      setFormError(error?.data?.message || "Failed to create department");
    }
  }, [
    departmentForm,
    createDepartment,
    resetDepartmentForm,
    refetchDepartments,
    showErrorAlert,
    setFormError,
    setShowModalDepartment,
    departments,
  ]);

  const handleUpdateDepartment = useCallback(async () => {
    // Clear any previous errors
    setFormError(null);

    // Validate department name
    if (!editDepartmentData.name?.trim()) {
      setFormError("Department name is required");
      return;
    }

    // Prevent renaming to "maintenance" (case-insensitive)
    if (editDepartmentData.name?.trim().toLowerCase() === "maintenance") {
      // Check if maintenance department already exists
      const maintenanceExists = departments?.some(
        (dept) => dept.name?.toLowerCase() === "maintenance",
      );

      if (maintenanceExists) {
        setFormError(
          "A 'maintenance' department already exists. Cannot rename to 'maintenance'.",
        );
        return;
      }
      // If no maintenance exists, prevent renaming to maintenance (it should be created fresh, not renamed)
      setFormError(
        "Cannot rename department to 'maintenance'. This name is reserved.",
      );
      return;
    }

    // Validate department description
    if (!editDepartmentData.description?.trim()) {
      setFormError("Department description is required");
      return;
    }

    try {
      await updateDepartment({
        id: selectedDepartmentId,
        name: editDepartmentData.name,
        description: editDepartmentData.description,
        academyId: academyId,
      }).unwrap();
      showSuccessToast("Department updated successfully");
      setShowEditDepartmentModal(false);
      setEditDepartmentData({ name: "", description: "" });
      setOriginalDepartmentData({ name: "", description: "" });
      setSelectedDepartmentId(null);
      refetchDepartments();
    } catch (error) {
      setFormError(error?.data?.message || "Failed to update department");
    }
  }, [
    editDepartmentData,
    selectedDepartmentId,
    academyId,
    updateDepartment,
    showSuccessToast,
    setFormError,
    setShowEditDepartmentModal,
    setEditDepartmentData,
    setOriginalDepartmentData,
    setSelectedDepartmentId,
    refetchDepartments,
    departments,
  ]);

  const handleDeleteDepartment = useCallback(
    async (
      departmentId,
      showConfirmDialog,
      deleteDepartment,
      refetchDepartments,
      showSuccessToast,
      showErrorAlert,
      departments = [],
    ) => {
      // Check if the department is "maintenance" (case-insensitive)
      const department = departments?.find(
        (d) => d.departmentId === departmentId,
      );
      if (department?.name?.toLowerCase() === "maintenance") {
        showErrorAlert("The 'maintenance' department cannot be deleted.");
        return;
      }

      const result = await showConfirmDialog(
        "Are you sure?",
        "Do you want to delete this department?",
      );

      if (result.isConfirmed) {
        try {
          await deleteDepartment(departmentId).unwrap();
          showSuccessToast("Department successfully deleted!");
          refetchDepartments();
        } catch (error) {
          showErrorAlert("Failed to delete department");
        }
      }
    },
    [],
  );

  const handleEditDepartment = useCallback(
    (
      departmentId,
      departments,
      setEditDepartmentData,
      setOriginalDepartmentData,
      setSelectedDepartmentId,
      setShowEditDepartmentModal,
      showErrorAlert,
    ) => {
      const department = departments?.find(
        (d) => d.departmentId === departmentId,
      );
      if (department) {
        // Prevent editing maintenance department
        if (department.name?.toLowerCase() === "maintenance") {
          showErrorAlert("The 'maintenance' department cannot be edited.");
          return;
        }
        const departmentData = {
          name: department.name,
          description: department.description || "",
        };
        setEditDepartmentData(departmentData);
        setOriginalDepartmentData(departmentData);
        setSelectedDepartmentId(departmentId);
        setShowEditDepartmentModal(true);
      }
    },
    [],
  );

  return {
    handleCreateDepartment,
    handleUpdateDepartment,
    handleDeleteDepartment,
    handleEditDepartment,
  };
};
